import { useMemo } from 'react';
import { useObservable, useSubscription } from 'observable-hooks';
import { RxCollection } from 'rxdb';
import { BehaviorSubject, combineLatest, of, switchMap, take } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import invariant from 'tiny-invariant';

import { PassengerBookingView } from '@mopla/data-models';

import { useBusinessLayer } from '../business-logic';
import { TPassengerBookingDocType } from '../schemas/passengerBooking';

import { TPassengerRxReplications } from './replications';

export interface IBookingsDBValue {
	data: PassengerBookingView[] | null;
	isReplicating: boolean;
	isReady: boolean;
}

export const usePassengerBookings$ = () => {
	const { db, replications } = useBusinessLayer();

	const replicationController = (replications as TPassengerRxReplications)
		.passenger_booking;

	invariant(
		replicationController,
		'No passengerBooking replicationController found.'
	);

	const subscription$ = useMemo(
		() =>
			new BehaviorSubject<IBookingsDBValue>({
				data: null,
				isReplicating: false,
				isReady: false,
			}),
		[]
	);

	/**
	 * Read bookings directly from the local RxDB collection — independent
	 * of the replication state. Replication start is gated on Firebase auth
	 * initialisation and the network (see {useManageRxDBReplications}); on a
	 * cold launch that can take tens of seconds on some iOS devices, during
	 * which `replicationController.replicationState$` stays `null`.
	 *
	 * Previously the bookings stream was sourced from
	 * `replicationState.collection.find().$`, so `isReady` could not flip to
	 * `true` until replication had started. That left the home page stuck on
	 * its initial-loading skeleton (and the splash up) for the duration of
	 * the auth/network handshake. RxDB persists the bookings on disk, so we
	 * can render the cached data immediately and let replication catch up in
	 * the background.
	 */
	const r$ = useObservable(() => {
		const passengerBookingCol = db.collections[
			'passenger_booking'
		] as RxCollection<TPassengerBookingDocType>;

		const passengerBookings$ = passengerBookingCol.find().$.pipe(
			/** Type assertion `as PassengerBookingView` is wrong, it is more performant than {toMutableJSON}
			 * https://rxdb.info/rx-document.html#tomutablejson */
			/** TODO this is a types hotfix */
			map((docs) =>
				docs.map((doc) => doc.toJSON() as unknown as PassengerBookingView)
			)
		);

		const isReady$ = passengerBookings$.pipe(
			take(1),
			map(() => true),
			startWith(false)
		);

		const isReplicating$ = replicationController.replicationState$.pipe(
			switchMap((state) => state?.active$ ?? of(false))
		);

		return combineLatest([passengerBookings$, isReady$, isReplicating$]).pipe(
			map(([data, isReady, isReplicating]) => ({
				data,
				isReady,
				isReplicating,
			}))
		);
	}, []);

	useSubscription(r$, (v) => {
		subscription$.next(v);
	});

	return subscription$;
};
